'use client'

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUpDown, Filter, MapPin } from 'lucide-react'
import { CountryFlag } from '@/lib/countryFlags'
import {
  buildAccordionRegions,
  getMultiLocationFilterDisplay,
  isEffectivelyAllLocations,
  uniqueFlagCountryForLocationSelections,
} from '@/lib/locationRegions'
import { useCities } from '@/hooks/useCities'
import type { BoardUrlFilterState } from '@/hooks/useBoardUrlFilters'
import { BoardFilterSheet } from './BoardFilterSheet'
import {
  filterPillClassName,
  filterPrimaryButtonClassName,
} from '@/components/shared/filterSheetStyles'

interface BoardFilterBarProps {
  appliedFilters: BoardUrlFilterState
  onApplyFilters: (draft: BoardUrlFilterState) => void
  filterSheetOpen: boolean
  onFilterSheetOpenChange: (open: boolean) => void
}

function FilterDisplayPill({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button type="button" className={filterPillClassName} onClick={onClick}>
      {icon}
      <span className="whitespace-nowrap">{label}</span>
    </button>
  )
}

export function buildBoardLocationLabel(
  selectedLocations: readonly string[],
  allLocationsText: string,
  launchedCities: readonly string[],
  locationAccordionRegions: ReturnType<typeof buildAccordionRegions>,
): string {
  if (isEffectivelyAllLocations([...selectedLocations], allLocationsText, launchedCities)) {
    return allLocationsText
  }
  return getMultiLocationFilterDisplay(
    [...selectedLocations],
    allLocationsText,
    locationAccordionRegions,
  )
}

export function BoardFilterBar({
  appliedFilters,
  onApplyFilters,
  filterSheetOpen,
  onFilterSheetOpenChange,
}: BoardFilterBarProps) {
  const { t } = useTranslation()
  const { cities: launchedCities, cityCountryByCity } = useCities()

  const locationAccordionRegions = React.useMemo(
    () => buildAccordionRegions(launchedCities, cityCountryByCity ?? {}),
    [launchedCities, cityCountryByCity],
  )

  const allLocationsText = t('onskin.filters.allLocations')

  const locationEffectivelyAll = isEffectivelyAllLocations(
    appliedFilters.selectedLocations,
    allLocationsText,
    launchedCities,
  )

  const locationLabel = buildBoardLocationLabel(
    appliedFilters.selectedLocations,
    allLocationsText,
    launchedCities,
    locationAccordionRegions,
  )

  const locationFlagCountry = uniqueFlagCountryForLocationSelections(
    appliedFilters.selectedLocations,
    allLocationsText,
    cityCountryByCity ?? {},
  )

  const sortLabel =
    appliedFilters.sortMode === 'recent'
      ? t('community.sortRecent')
      : t('community.sortTopWeek')

  const postTypeLabel =
    appliedFilters.postTypeFilter === 'all'
      ? t('board.filters.allPostTypes')
      : t(`board.postTypes.${appliedFilters.postTypeFilter}`)

  const openFilterSheet = () => onFilterSheetOpenChange(true)

  return (
    <>
      <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar">
        <button
          type="button"
          className={filterPrimaryButtonClassName}
          aria-label={t('board.filters.open', 'Open filters')}
          onClick={openFilterSheet}
        >
          <Filter className="h-4 w-4" />
        </button>

        <FilterDisplayPill
          icon={
            locationEffectivelyAll ? (
              <MapPin className="h-4 w-4" />
            ) : (
              <CountryFlag
                country={locationFlagCountry ?? undefined}
                size="sm"
                showBorder={false}
                className="h-4 w-4 shrink-0 !rounded-sm"
              />
            )
          }
          label={locationLabel}
          onClick={openFilterSheet}
        />

        <FilterDisplayPill
          icon={<ArrowUpDown className="h-4 w-4" />}
          label={sortLabel}
          onClick={openFilterSheet}
        />

        {appliedFilters.postTypeFilter !== 'all' && (
          <FilterDisplayPill
            icon={<Filter className="h-4 w-4" />}
            label={postTypeLabel}
            onClick={openFilterSheet}
          />
        )}
      </div>

      {filterSheetOpen ? (
        <BoardFilterSheet
          onOpenChange={onFilterSheetOpenChange}
          appliedFilters={appliedFilters}
          onApply={onApplyFilters}
        />
      ) : null}
    </>
  )
}
